import React, { useState } from 'react';
import { Card, Upload, Button, Space, Typography, List, message, Popconfirm } from 'antd';
import {
    UploadOutlined,
    DeleteOutlined,
    DatabaseOutlined,
    CheckCircleOutlined,
} from '@ant-design/icons';
import type { UploadFile, UploadProps } from 'antd';

const { Text, Title } = Typography;

type SqliteUploadSectionProps = {
    templateUuid: string | null;
    uploadedFiles: string[];
    onUploadSuccess: (objectName: string) => void;
    onDeleteFile: (objectName: string) => void;
    onUpload: (file: File) => Promise<{ objectName: string }>;
};

export const SqliteUploadSection: React.FC<SqliteUploadSectionProps> = ({
    templateUuid,
    uploadedFiles,
    onUploadSuccess,
    onDeleteFile,
    onUpload,
}) => {
    const [uploading, setUploading] = useState(false);

    const handleUpload: UploadProps['customRequest'] = async (options) => {
        const { file, onSuccess, onError } = options;

        try {
            setUploading(true);
            const result = await onUpload(file as File);
            onUploadSuccess(result.objectName);
            onSuccess?.(result);
        } catch (error) {
            console.error('Upload error:', error);
            message.error(`Tải lên ${(file as File).name} thất bại`);
            onError?.(error as Error);
        } finally {
            setUploading(false);
        }
    };

    const beforeUpload = (file: File) => {
        const isSqlite = file.name.endsWith('.sqlite') || file.name.endsWith('.db');
        if (!isSqlite) {
            message.error('Chỉ cho phép tải lên file cơ sở dữ liệu SQLite (.sqlite hoặc .db)');
            return false;
        }

        const isLt50M = file.size / 1024 / 1024 < 50;
        if (!isLt50M) {
            message.error('Kích thước file phải nhỏ hơn 50MB');
            return false;
        }

        return true;
    };

    return (
        <div style={{ padding: '16px', height: '100%', overflowY: 'auto' }}>
            <Title level={5} style={{ marginBottom: 16 }}>
                <Space>
                    <DatabaseOutlined />
                    <span>Cơ sở dữ liệu</span>
                </Space>
            </Title>
            <Space direction="vertical" style={{ width: '100%' }} size="middle">
                <Upload
                    customRequest={handleUpload}
                    beforeUpload={beforeUpload}
                    showUploadList={false}
                    accept=".sqlite,.db"
                >
                    <Button
                        icon={<UploadOutlined />}
                        loading={uploading}
                        block
                        type="dashed"
                    >
                        Tải lên SQLite
                    </Button>
                </Upload>

                {uploadedFiles.length > 0 && (
                    <>
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                            Đã tải lên ({uploadedFiles.length}):
                        </Text>
                        <List
                            size="small"
                            bordered
                            dataSource={uploadedFiles}
                            renderItem={(file) => (
                                <List.Item
                                    actions={[
                                        <Popconfirm
                                            title="Xóa cơ sở dữ liệu này?"
                                            description="Các trường sử dụng CSDL này sẽ mất nguồn dữ liệu."
                                            onConfirm={() => onDeleteFile(file)}
                                            okText="Xóa"
                                            cancelText="Hủy"
                                            okButtonProps={{ danger: true }}
                                        >
                                            <Button
                                                type="text"
                                                size="small"
                                                danger
                                                icon={<DeleteOutlined />}
                                            />
                                        </Popconfirm>,
                                    ]}
                                >
                                    <Space>
                                        <CheckCircleOutlined style={{ color: '#52c41a' }} />
                                        <Text style={{ fontSize: '12px' }}>{file.split('/').pop()}</Text>
                                    </Space>
                                </List.Item>
                            )}
                        />
                    </>
                )}

                {uploadedFiles.length === 0 && (
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                        Chưa có CSDL nào. Tải lên file SQLite để sử dụng với trường chọn liên kết.
                    </Text>
                )}
            </Space>
        </div>
    );
};
